import { CheckIcon, CopyIcon } from "lucide-react";
import moment from "moment";
import { useState } from "react";

import {
  CATEGORY_BADGE_VARIANT,
  CATEGORY_LABEL,
  SEVERITY_DOT_CLASSES,
  getTaxonomy,
} from "./eventTaxonomy";

import { Badge, BadgeSize } from "@/components/ui/Badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { IdentityAuditEvent } from "@/queries/identity/useIdentityAuditEvents";

interface Props {
  event: IdentityAuditEvent | null;
  onClose: () => void;
}

const formatUserDisplay = (email: string | null, userId: number | null): string | null => {
  if (email) {
    return userId !== null ? `${email} (#${userId})` : email;
  }
  if (userId !== null) {
    return `#${userId}`;
  }
  return null;
};

const CopyButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
      aria-label="Copy value"
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <CopyIcon className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

const Field = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null;
  mono?: boolean;
}) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="w-32 shrink-0 text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="flex flex-1 items-start gap-2 break-all">
        <span className={`flex-1 text-sm text-gray-900 ${mono ? "font-mono text-xs" : ""}`}>
          {value}
        </span>
        <CopyButton value={String(value)} />
      </div>
    </div>
  );
};

/** Right-side drawer showing details for a single audit event. */
export const AuditEventDrawer = ({ event, onClose }: Props) => {
  const isOpen = event !== null;
  const taxonomy = event ? getTaxonomy(event.event_type) : null;
  const [payloadCopied, setPayloadCopied] = useState(false);

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:!max-w-xl sm:max-w-md">
        {event && taxonomy && (
          <>
            <SheetHeader className="border-b border-gray-100 pb-4">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${SEVERITY_DOT_CLASSES[taxonomy.severity]}`}
                />
                <Badge
                  variant={CATEGORY_BADGE_VARIANT[taxonomy.category]}
                  size={BadgeSize.SM}
                  text={CATEGORY_LABEL[taxonomy.category]}
                  className="uppercase tracking-wide"
                />
              </div>
              <SheetTitle className="text-lg">{taxonomy.label}</SheetTitle>
              <SheetDescription className="font-mono text-xs">{event.event_type}</SheetDescription>
            </SheetHeader>

            <div className="px-4 pb-4">
              <div className="divide-y divide-gray-100">
                <Field
                  label="Occurred"
                  value={moment(event.created_at).format("MMM D, YYYY [at] h:mm:ss A")}
                />
                <Field label="Raw ISO" value={event.created_at} mono />
                <Field label="Source" value={event.source} mono />
                <Field
                  label="Actor"
                  value={formatUserDisplay(event.actor_email, event.actor_user_id)}
                />
                <Field
                  label="Target"
                  value={formatUserDisplay(event.target_email, event.target_user_id)}
                />
                <Field
                  label="Workspace"
                  value={event.workspace_id !== null ? `#${event.workspace_id}` : null}
                />
                <Field label="Connection" value={event.connection_id} mono />
                <Field label="WorkOS event" value={event.workos_event_id} mono />
                <Field label="Request" value={event.request_id} mono />
                <Field label="IdP request" value={event.idp_request_id} mono />
              </div>

              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Payload
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(JSON.stringify(event.payload, null, 2))
                        .then(() => {
                          setPayloadCopied(true);
                          window.setTimeout(() => setPayloadCopied(false), 1500);
                        });
                    }}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                  >
                    {payloadCopied ? (
                      <>
                        <CheckIcon className="h-3.5 w-3.5 text-green-600" /> Copied
                      </>
                    ) : (
                      <>
                        <CopyIcon className="h-3.5 w-3.5" /> Copy JSON
                      </>
                    )}
                  </button>
                </div>
                <pre className="max-h-[40vh] overflow-auto rounded bg-gray-50 p-3 font-mono text-[11px] text-gray-700">
                  {JSON.stringify(event.payload ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
